import {
    useEffect,
    useCallback,
    useState,
    useRef,
    ReactElement,
    useMemo,
} from 'react'
import { Box, Button, Typography, debounce } from '@mui/material'
import ReactFlow, {
    Background,
    Controls,
    ReactFlowProvider,
    addEdge,
    useNodesState,
    useEdgesState,
    MarkerType,
    updateEdge,
    Edge,
    Node,
    Connection,
} from 'reactflow'
import 'reactflow/dist/style.css'
import AvailableWorkflows from './AvailableWorkflows'
import { addWorkFlow, fetchWorkflows } from '../../../services/dtServices'
import { useAppSelector } from '../../../redux/hook'
import { IWorkflowItem, IEdge, IConfiguredNode, IPosition } from '../interfaces'
import LogoNode from './LogoNode'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '../../../hooks/usePermissions'
import { UserPermissions } from '../../../constants/constants'

const nodeTypes = { logoNode: LogoNode }

const Workflows = (): ReactElement => {
    const { t } = useTranslation()
    const [nodes, setNodes, onNodesChange] = useNodesState([])
    const [edges, setEdges, onEdgesChange] = useEdgesState([])
    const reactFlowInstanceRef = useRef<any>(null) // Ref to store the React Flow instance
    const [workFlowItems, setWorkFlowItems] = useState<IWorkflowItem[]>([])
    const [availableWorkFlows, setAvailableWorkFlows] = useState<
        IWorkflowItem[]
    >([])

    const edgeUpdateSuccessful = useRef(true) //To implement Delete Edge on Drop
    const [selectedNode, setSelectedNode] = useState<string | null>(null)
    const isInitialFetch = useRef(true)

    const org = useAppSelector((state) => state.org.org)
    const { selectedEntity } = useAppSelector((state) => state.dtStudio)
    const { id: entityId } = selectedEntity
    const orgId = org?.id ? org?.id : ''
    const { hasPermission } = usePermissions()
    const hasWriteAccess = useMemo(() => {
        return (
            !!selectedEntity?.ownTemplate &&
            hasPermission(UserPermissions.ENTITY_WRITER)
        )
    }, [selectedEntity?.ownTemplate, hasPermission])
    const getEntityNameById = (id: string, arr: any[]) => {
        return arr?.find((i) => i.instanceId === id)?.name || ''
    }

    const saveWorkflow = useCallback(
        debounce(async (nodes: Node[], edges: Edge[]) => {
            if (!isInitialFetch.current) {
                const payloadEdges: IEdge[] = edges?.map((edge) => ({
                    src: edge.source,
                    dest: edge.target,
                }))
                const payloadNodes: IConfiguredNode[] = nodes?.map((node) => ({
                    id: node.id,
                    position: node.position as unknown as IPosition,
                }))
                const payload = {
                    edges: payloadEdges,
                    configuredNodes: payloadNodes,
                }
                await addWorkFlow(entityId, orgId, payload)
            }
            isInitialFetch.current = false
        }, 500),
        [entityId, org?.id],
    )

    useEffect(() => {
        isInitialFetch.current = true
    }, [org?.id])

    useEffect(() => {
        saveWorkflow(nodes, edges)
    }, [nodes, edges, saveWorkflow])

    useEffect(() => {
        const fetchConfiguredWorkflows = async () => {
            const data = await fetchWorkflows(entityId, orgId)
            const initialEdges: Edge[] =
                data.edges?.map((edge: IEdge) => ({
                    source: edge.src,
                    target: edge.dest,
                    id: `${edge.src}-${edge.dest}`,
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: '#00369C',
                    },
                    // type: ConnectionLineType.SmoothStep,
                    // animated: true,
                })) || []

            const initialNodes: Node[] =
                data.configuredNodes?.map((node: IConfiguredNode) => ({
                    id: node.id.toString(),
                    data: {
                        label: getEntityNameById(
                            node.id.toString(),
                            data?.allNodes || [],
                        ),
                        id: node.id.toString(),
                        logo: node.logo,
                    },
                    position: {
                        x: Number(node.position.x),
                        y: Number(node.position.y),
                    }, // Placeholder positions
                    deletable: true,
                    type: 'logoNode',
                })) || []
            setNodes(initialNodes)
            setEdges(initialEdges)
            isInitialFetch.current = true
            let totalWorkFlows: IWorkflowItem[] = []
            let availableWorkFlows: IWorkflowItem[] = []
            data.allNodes?.forEach((node) => {
                const nodeData = {
                    id: node.instanceId?.toString(),
                    name: node.name,
                    logo: node.logo,
                }
                totalWorkFlows = [...totalWorkFlows, nodeData]

                const isNodeAlreadyConfigured = data.configuredNodes?.find(
                    (cn) => cn.id === node.instanceId,
                )
                if (isNodeAlreadyConfigured) {
                    /**
                     * excluding the nodes(workflows) that are already configured
                     */
                    return
                } else {
                    availableWorkFlows = [...availableWorkFlows, nodeData]
                }
            })

            setWorkFlowItems(totalWorkFlows)
            setAvailableWorkFlows(availableWorkFlows)
        }
        fetchConfiguredWorkflows()
        //Should we add entityId as a dependency here?
    }, [entityId])

    const onConnect = useCallback(
        (params: any) => {
            const newEdge = {
                ...params,
                id: `${params.source}-${params.target}`,
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#00369C',
                },
                // type: ConnectionLineType.SmoothStep,
                // animated: true,
            }
            setEdges((prevEdges) => addEdge(newEdge, prevEdges))
        },
        [nodes, setEdges],
    )

    const onEdgeUpdateStart = useCallback(() => {
        edgeUpdateSuccessful.current = false
    }, [])

    const onEdgeUpdateEnd = useCallback((_: any, edge: Edge) => {
        if (!edgeUpdateSuccessful.current) {
            setEdges((edges) => edges.filter((e) => e.id !== edge.id))
        }
        edgeUpdateSuccessful.current = true
    }, [])

    // gets called after end of edge gets dragged to another source or target
    const onEdgeUpdate = useCallback(
        (oldEdge: Edge, newConnection: Connection) => {
            edgeUpdateSuccessful.current = true
            setEdges((els) => updateEdge(oldEdge, newConnection, els))
        },
        [],
    )

    const onDragOver = useCallback((event: any) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
    }, [])

    /**
     * Below method is called when an available workflow is dragged onto reactflow canvas
     */

    const onDrop = useCallback(
        (event: any) => {
            event.preventDefault()

            const data = event.dataTransfer.getData('application/reactflow')
            // check if the dropped element is valid
            if (typeof data === 'undefined' || !data) {
                return
            }
            const nodeJSON = JSON.parse(data)

            // reactFlowInstance.project was renamed to reactFlowInstance.screenToFlowPosition
            // and you don't need to subtract the reactFlowBounds.left/top anymore
            // details: https://reactflow.dev/whats-new/2023-11-10
            const position = reactFlowInstanceRef.current.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            })
            const newNode = {
                id: nodeJSON.id,
                position,
                data: {
                    label: nodeJSON.name,
                    id: nodeJSON.id,
                    logo: nodeJSON.logo,
                },
                type: 'logoNode',
            }
            setNodes((prevNodes) => [...prevNodes, newNode])
            setAvailableWorkFlows((prevWorkflows) =>
                prevWorkflows.filter((workflow) => workflow.id !== nodeJSON.id),
            )
        },
        [
            reactFlowInstanceRef,
            nodes,
            availableWorkFlows,
            setAvailableWorkFlows,
        ],
    )

    // Handle node click to set selected node
    const onNodeClick = useCallback((event: any, node: any) => {
        setSelectedNode(node.id)
    }, [])

    // Handle key press for deleting node
    const handleKeyPress = useCallback(
        (event: any) => {
            if (event.key === 'Delete' && selectedNode) {
                const updatedNodes = nodes.filter(
                    (node) => node.id !== selectedNode,
                )
                setNodes(updatedNodes)
                setSelectedNode(null)
            }
        },
        [selectedNode, nodes],
    )

    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress)
        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }, [handleKeyPress])

    // Delete selected node and it's edges when delete button is clicked
    const handleDeleteClick = () => {
        if (selectedNode) {
            // Filter out the selected node
            const updatedNodes = nodes.filter(
                (node) => node.id !== selectedNode,
            )

            // Filter out the edges connected to the selected node
            const updatedEdges = edges.filter(
                (edge) =>
                    edge.source !== selectedNode &&
                    edge.target !== selectedNode,
            )

            //adding deleted node in availableWorkFlows
            const deletedNodeDetails = workFlowItems.find(
                (i) => i.id === selectedNode,
            )

            const updatedAvailableWorkflows = deletedNodeDetails
                ? [...availableWorkFlows, deletedNodeDetails]
                : [...availableWorkFlows]

            setNodes(updatedNodes)
            setEdges(updatedEdges)
            setSelectedNode(null)

            setAvailableWorkFlows(updatedAvailableWorkflows)
        }
    }

    return (
        <Box maxHeight={'calc(100vh - 370px)'} overflow="auto">
            <Box display="flex" height="100%" minHeight={'500px'}>
                <AvailableWorkflows
                    availableWorkFlows={availableWorkFlows}
                    hasWriteAccess={hasWriteAccess}
                />
                <Box
                    maxWidth={'10%'}
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    p={2}
                >
                    <img
                        src={`/assets/images/icons/drag-and-drop-hand-icon.svg`}
                        alt={'drag-and-drop-hand-icon'}
                        loading="lazy"
                    />
                    <Typography
                        variant="body1"
                        textAlign={'center'}
                        sx={{ mt: 1 }}
                    >
                        {t('dragDropWorkflows')}
                    </Typography>
                </Box>
                <Box
                    flex={1}
                    p={2}
                    style={{
                        position: 'relative',
                        border: '1px solid rgba(0, 0, 0, 0.2)',
                    }}
                >
                    <ReactFlowProvider>
                        <ReactFlow
                            nodes={nodes}
                            edges={edges}
                            onNodesChange={onNodesChange}
                            onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onInit={(instance) => {
                                reactFlowInstanceRef.current = instance
                                instance.fitView() // Fit view when initialized
                            }}
                            onEdgeUpdate={onEdgeUpdate}
                            onEdgeUpdateStart={
                                hasWriteAccess ? onEdgeUpdateStart : undefined
                            }
                            onEdgeUpdateEnd={
                                hasWriteAccess ? onEdgeUpdateEnd : undefined
                            }
                            onDrop={hasWriteAccess ? onDrop : undefined}
                            onDragOver={hasWriteAccess ? onDragOver : undefined}
                            onNodeClick={
                                hasWriteAccess ? onNodeClick : undefined
                            }
                            fitView
                            style={{ width: '100%', height: '100%' }}
                            nodeTypes={nodeTypes}
                        >
                            <Background />
                            <Controls showInteractive={false} />
                        </ReactFlow>
                        {selectedNode && hasWriteAccess && (
                            <Box position="absolute" top={0} right={0} p={2}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleDeleteClick}
                                    id="delete-node"
                                >
                                    {t('deleteNode')}
                                </Button>
                            </Box>
                        )}
                    </ReactFlowProvider>
                </Box>
            </Box>
        </Box>
    )
}

export default Workflows
